import React from "react";
import { Select, SearchAndInsert, TrialBanner, AccountSettings, Header } from "@cuntas-mitool/ui";
import {
	Button,
	makeStyles,
	tokens,
	Text,
} from "@fluentui/react-components";
import { Checkmark16Regular } from "@fluentui/react-icons";
import {
	useClasses,
	useSubjects,
	useSkillsAndConcepts,
	useStrandData,
	useStrandUnits,
	useLearningOutcomes,
} from "@cuntas-mitool/ui";
import { useAuth } from "../context/WebAuthContext";
import { useSubscription } from "@cuntas-mitool/ui";

/**
 * Strip HTML tags from a string and capitalize the first letter.
 */
function cleanText(html: string): string {
	const text = html.replace(/<[^>]*>/g, "").trim();
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1);
}

const useStyles = makeStyles({
	container: {
		padding: tokens.spacingHorizontalM,
	},
	segmented: {
		display: "inline-flex",
		backgroundColor: tokens.colorNeutralBackground3,
		borderRadius: tokens.borderRadiusMedium,
		padding: tokens.spacingHorizontalXXS,
		marginBottom: tokens.spacingVerticalM,
	},
	errorText: {
		color: tokens.colorPaletteRedForeground1,
		marginTop: tokens.spacingVerticalS,
	},
	toast: {
		display: "flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalXS,
		backgroundColor: tokens.colorPaletteGreenBackground1,
		color: tokens.colorPaletteGreenForeground1,
		padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
		borderRadius: tokens.borderRadiusMedium,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightSemibold,
		position: "fixed",
		bottom: tokens.spacingVerticalXL,
		left: "50%",
		transform: "translateX(-50%)",
		boxShadow: tokens.shadow4,
		zIndex: 1000,
	},
});

type View = "curriculum" | "account";

function WebTool() {
	const styles = useStyles();
	const { isAuthenticated, error } = useAuth();
	const { openCheckout } = useSubscription();
	const [view, setView] = React.useState<View>("curriculum");
	const [copiedToast, setCopiedToast] = React.useState(false);

	const { selectedClass, setSelectedClass, classesData, classesLoading, classesError } =
		useClasses();
	const {
		selectedSubject,
		setSelectedSubject,
		subjectsData,
		subjectsLoading,
		subjectsError,
	} = useSubjects(selectedClass);

	const [mode, setMode] = React.useState<"outcomes" | "skills">("outcomes");

	const { strandData, selectedStrand, setSelectedStrand, strandLoading, strandError } =
		useStrandData(selectedClass, selectedSubject, mode === "outcomes");

	const {
		strandUnitsData,
		selectedStrandUnit,
		setSelectedStrandUnit,
		strandUnitsLoading,
		strandUnitsError,
	} = useStrandUnits(selectedClass, selectedSubject, selectedStrand);

	const { skillsAndConceptsData } = useSkillsAndConcepts(selectedClass, selectedSubject);

	const { learningOutcomesData } = useLearningOutcomes(
		selectedClass,
		selectedSubject,
		selectedStrand,
		selectedStrandUnit,
		mode === "outcomes",
	);

	const copyToClipboard = React.useCallback(async (text: string): Promise<void> => {
		try {
			await navigator.clipboard.writeText(text);
		} catch {
			// Fallback for browsers without Clipboard API
			const el = document.createElement("textarea");
			el.value = text;
			el.style.position = "fixed";
			el.style.opacity = "0";
			document.body.appendChild(el);
			el.select();
			document.execCommand("copy");
			document.body.removeChild(el);
		}
		setCopiedToast(true);
		setTimeout(() => setCopiedToast(false), 2000);
	}, []);

	return (
		<>
			<Header onAccountSettings={() => setView("account")} />

			{/* Trial banner */}
			<TrialBanner onSubscribeClick={openCheckout} />

			{error && (
				<Text className={styles.errorText}>{error}</Text>
			)}

			{/* Account Settings View */}
			{view === "account" && isAuthenticated ? (
				<AccountSettings onBack={() => setView("curriculum")} />
			) : (
				<div className={styles.container}>
					<Select
						options={classesData}
						label="Select a class"
						value={selectedClass}
						onChange={(newClass) =>
							!Array.isArray(newClass) && setSelectedClass(newClass)
						}
						loading={classesLoading}
						error={classesError}
						copyLabel="class name"
					/>

					{selectedClass && (
						<Select
							options={subjectsData}
							label="Select a subject"
							value={selectedSubject}
							onChange={(newSubject) =>
								!Array.isArray(newSubject) && setSelectedSubject(newSubject)
							}
							loading={subjectsLoading}
							error={subjectsError}
							copyLabel="subject name"
						/>
					)}

					{selectedClass && selectedSubject && (
						<div className={styles.segmented}>
							<Button
								appearance={mode === "outcomes" ? "primary" : "subtle"}
								onClick={() => setMode("outcomes")}
							>
								Learning Outcomes
							</Button>
							<Button
								appearance={mode === "skills" ? "primary" : "subtle"}
								onClick={() => setMode("skills")}
							>
								Skills & Concepts
							</Button>
						</div>
					)}

					{mode === "skills" &&
						selectedClass &&
						selectedSubject &&
						skillsAndConceptsData?.data && (
							<SearchAndInsert
								label="Skills & Concepts"
								options={skillsAndConceptsData.data.map((item) => ({
									id: item.id,
									text: cleanText(item.subjectExtra),
								}))}
								placeholder="Search skills and concepts..."
								onAction={copyToClipboard}
								showCopyTooltip
							/>
						)}

					{mode === "outcomes" && selectedClass && selectedSubject && (
						<>
							<Select
								options={strandData}
								label="Select a strand"
								value={selectedStrand}
								onChange={(newStrand) =>
									!Array.isArray(newStrand) && setSelectedStrand(newStrand)
								}
								loading={strandLoading}
								error={strandError}
								copyLabel="strand name"
							/>
							{selectedStrand && (
								<Select
									options={strandUnitsData}
									label="Select a strand unit"
									value={selectedStrandUnit}
									onChange={(newStrandUnit) =>
										!Array.isArray(newStrandUnit) &&
										setSelectedStrandUnit(newStrandUnit)
									}
									loading={strandUnitsLoading}
									error={strandUnitsError}
									copyLabel="strand unit name"
								/>
							)}
							{selectedStrandUnit && (
								<SearchAndInsert
									label="Learning Outcomes"
									options={learningOutcomesData.map((item) => ({
										id: item.key,
										text: cleanText(item.text),
									}))}
									placeholder="Search learning outcomes..."
									onAction={copyToClipboard}
									showCopyTooltip
								/>
							)}
						</>
					)}
				</div>
			)}

			{/* Copied toast notification */}
			{copiedToast && (
				<div className={styles.toast}>
					<Checkmark16Regular />
					Copied to clipboard
				</div>
			)}
		</>
	);
}

export default WebTool;
